import React, { useEffect, useState, Children, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { ChevronRight, ArrowLeft, CheckCircle2, Loader2, MessageCircle, X, Smartphone, UtensilsCrossed, BarChart3, Crown, Zap, QrCode } from 'lucide-react';
// --- Background Elements ---
const FLOATING_FOODS = ['🍔', '🍕', '🍣', '☕', '🍟', '🌮', '🍰', '🥗', '🍩', '🥑'];
const BACKGROUND_WORDS = ['Pedidos', 'WhatsApp', 'Delivery', 'Marketplace', 'KDS', 'Tracking', 'Operación', 'Tecnología', 'Restaurantes', 'Crecimiento', 'Control', 'Inventario'];
// --- Form Types ---
type BusinessType = 'Restaurante' | 'Cafetería' | 'Pizzería' | 'Comida rápida' | 'Panadería' | 'Otro';
type MainGoal = 'Más pedidos' | 'Mejor control de cocina' | 'Menú digital' | 'Delivery / express' | 'Orden y operación' | 'Todo lo anterior';
interface FormData {
  owner_name: string;
  restaurant_name: string;
  whatsapp: string;
  social_handle: string;
  business_type: BusinessType | '';
  uses_whatsapp_orders: boolean | null;
  has_digital_menu: boolean | null;
  main_goal: MainGoal[];
}
export function OnboardingPage() {
  const [step, setStep] = useState(0); // 0 = Hero, 1-5 = Form, 6 = Success
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    owner_name: '',
    restaurant_name: '',
    whatsapp: '',
    social_handle: '',
    business_type: '',
    uses_whatsapp_orders: null,
    has_digital_menu: null,
    main_goal: []
  });
  // --- Handlers ---
  const handleNext = () => setStep(s => Math.min(s + 1, 6));
  const handleBack = () => setStep(s => Math.max(s - 1, 0));
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const cleanWhatsapp = formData.whatsapp.replace(/\D/g, '');
      if (!formData.owner_name || !formData.restaurant_name || !cleanWhatsapp || !formData.business_type) {
        throw new Error('Faltan campos obligatorios');
      }
      const {
        error
      } = await supabase.from('restaurant_applications').insert([{
        owner_name: formData.owner_name.trim(),
        restaurant_name: formData.restaurant_name.trim(),
        whatsapp: cleanWhatsapp,
        social_handle: formData.social_handle?.trim() || null,
        business_type: formData.business_type,
        uses_whatsapp_orders: formData.uses_whatsapp_orders ?? false,
        has_digital_menu: formData.has_digital_menu ?? false,
        main_goal: formData.main_goal.join(', ') || null,
        status: 'pending'
      }]);
      if (error) {
        console.error('❌ Error guardando aplicación:', error);
        throw error;
      }
      console.log('✅ Aplicación guardada correctamente');
      // Open WhatsApp with pre-filled message
      const message = encodeURIComponent(`Hola, acabo de aplicar a NOIZA.\nRestaurante: ${formData.restaurant_name}`);
      window.open(`https://wa.me/50688118105?text=${message}`, '_blank');
      setStep(6); // Success
    } catch (err: any) {
      console.error('❌ Error en submit:', err);
      setError('Hubo un error al enviar tu solicitud. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // --- Step Validation ---
  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.owner_name.trim().length > 0 && formData.restaurant_name.trim().length > 0;
      case 2:
        return formData.whatsapp.trim().length >= 8;
      case 3:
        return formData.business_type !== '';
      case 4:
        return formData.uses_whatsapp_orders !== null && formData.has_digital_menu !== null;
      case 5:
        return formData.main_goal.length > 0;
      default:
        return true;
    }
  };
  // --- Render Helpers ---
  const renderFloatingBackground = () => <div className="fixed inset-0 overflow-hidden pointer-events-none z-0" data-id="element-7341" data-mp="2zxsmr5hhyvqf">
      {/* Dark gradient base */}
      <div className="absolute inset-0 bg-[#0B0B0B]" data-id="element-7342" data-mp="2uyob8873olwx" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(201,163,78,0.15)_0%,rgba(11,11,11,1)_70%)]" data-id="element-7343" data-mp="2uwv0agpgffj4" />

      {/* Floating Words */}
      {BACKGROUND_WORDS.map((word, i) => <motion.div key={`word-${i}`} className="absolute text-white/5 font-black text-4xl md:text-6xl lg:text-8xl whitespace-nowrap select-none" initial={{
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      rotate: -15 + Math.random() * 30
    }} animate={{
      x: [null, Math.random() * window.innerWidth],
      y: [null, Math.random() * window.innerHeight]
    }} transition={{
      duration: 40 + Math.random() * 40,
      repeat: Infinity,
      repeatType: 'reverse',
      ease: 'linear'
    }} data-id="element-7344" data-mp="228fpk6dcvjdl" data-mpi={i}>
          {word}
        </motion.div>)}

      {/* Floating Foods */}
      {FLOATING_FOODS.map((food, i) => <motion.div key={`food-${i}`} className="absolute text-4xl md:text-6xl opacity-40 blur-[1px]" initial={{
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      scale: 0.5 + Math.random() * 1
    }} animate={{
      y: [null, Math.random() * window.innerHeight],
      rotate: [0, 360]
    }} transition={{
      y: {
        duration: 20 + Math.random() * 20,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'easeInOut'
      },
      rotate: {
        duration: 30 + Math.random() * 30,
        repeat: Infinity,
        ease: 'linear'
      }
    }} data-id="element-7345" data-mp="3q5ovd0li0pl5" data-mpi={i}>
          {food}
        </motion.div>)}
    </div>;
  const renderHowItWorksModal = () => <AnimatePresence data-id="element-7346" data-mp="u8hc932a6pxb">
      {showHowItWorks && <motion.div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} data-id="element-7347" data-mp="q8gxtif6meq9">
          {/* Backdrop */}
          <motion.div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowHowItWorks(false)} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-7348" data-mp="bchoy5k90ak2" />

          {/* Modal Content */}
          <motion.div className="relative z-10 w-full max-w-3xl mx-4 my-8 sm:my-16 bg-[#141414] border border-white/10 rounded-3xl overflow-hidden shadow-2xl" initial={{
        scale: 0.9,
        opacity: 0,
        y: 40
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.9,
        opacity: 0,
        y: 40
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} data-id="element-7349" data-mp="3jkz9fk7h3oay">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-[#141414]/95 backdrop-blur-md border-b border-white/10 px-6 py-5 flex items-center justify-between" data-id="element-7350" data-mp="3empcb426udmp">
              <div data-id="element-7351" data-mp="ox3rim988qbt">
                <h2 className="text-2xl font-bold text-white" data-id="element-7352" data-mp="ozpz2lw4b5u1">
                  ¿Cómo funciona NOIZA?
                </h2>
                <p className="text-sm text-white/50 mt-1" data-id="element-7353" data-mp="5jwlr7l9uwms">
                  Explicado de forma simple
                </p>
              </div>
              <button onClick={() => setShowHowItWorks(false)} className="p-2 hover:bg-white/10 rounded-xl transition-colors" data-id="element-7354" data-mp="mcv187e0rdy">
                <X className="w-6 h-6 text-white/60" data-id="element-7355" data-mp="9lhucgqrwor6" />
              </button>
            </div>

            <div className="p-6 sm:p-8 space-y-10" data-id="element-7356" data-mp="oa4i96uguou0">
              {/* What is NOIZA */}
              <div data-id="element-7357" data-mp="jbt8ew468ebs">
                <h3 className="text-lg font-bold text-[#C9A34E] mb-3 flex items-center gap-2" data-id="element-7358" data-mp="jcckhw7548hw">
                  <Zap className="w-5 h-5" data-id="element-7359" data-mp="xblyqqbscsj8" />
                  ¿Qué es NOIZA?
                </h3>
                <p className="text-white/70 leading-relaxed" data-id="element-7360" data-mp="t9h7tzcq2a0a">
                  NOIZA es una plataforma digital para restaurantes. En vez de
                  recibir pedidos por WhatsApp de forma desordenada, tus
                  clientes escanean un{' '}
                  <strong className="text-white" data-id="element-7361" data-mp="2v3ghyhe3ylbe">código QR</strong>, ven tu menú
                  en su celular, hacen su pedido, y este llega directo a tu
                  cocina organizado. Sin confusiones, sin errores, sin estrés.
                </p>
              </div>

              {/* How it works step by step */}
              <div data-id="element-7362" data-mp="2g66u8r9v5fra">
                <h3 className="text-lg font-bold text-[#C9A34E] mb-4 flex items-center gap-2" data-id="element-7363" data-mp="2g5f0llztts3n">
                  <Smartphone className="w-5 h-5" data-id="element-7364" data-mp="218wlvqsiopzs" />
                  Paso a paso
                </h3>
                <div className="space-y-4" data-id="element-7365" data-mp="269c00kkmi1it">
                  {[{
                num: '1',
                title: 'Subimos tu menú',
                desc: 'Nos pasas tu menú (fotos, precios, categorías) y nosotros lo montamos en la plataforma. Queda bonito y profesional.'
              }, {
                num: '2',
                title: 'Te damos un QR y un link',
                desc: 'Tu restaurante tiene su propia página web con tu menú. Tus clientes pueden acceder escaneando un QR en la mesa o desde un link en tus redes.'
              }, {
                num: '3',
                title: 'El cliente hace su pedido',
                desc: 'Tu cliente ve el menú, elige lo que quiere, personaliza opciones (tamaño, extras, salsas) y confirma su pedido desde su celular.'
              }, {
                num: '4',
                title: 'El pedido llega a tu cocina',
                desc: 'En tu pantalla de cocina (KDS) aparece el pedido automáticamente. Sin papelitos, sin gritos, sin confusiones. Lo marcas como listo cuando sale.'
              }, {
                num: '5',
                title: 'Tú controlas todo',
                desc: 'Desde tu panel de admin ves las ventas del día, métricas, puedes pausar la cocina si estás lleno, activar/desactivar platillos, y más.'
              }].map(step => <div key={step.num} className="flex gap-4" data-id="element-7366" data-mp="2tqroydnsqykj">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#C9A34E]/20 text-[#C9A34E] font-bold text-sm flex items-center justify-center" data-id="element-7367" data-mp="2tsb9whkedxsn">
                        {step.num}
                      </div>
                      <div data-id="element-7368" data-mp="2f12lucw58khm">
                        <p className="font-semibold text-white" data-id="element-7369" data-mp="2f2dp8blofq87">{step.title}</p>
                        <p className="text-sm text-white/60 mt-0.5" data-id="element-7370" data-mp="2a2rce39rgbzx">
                          {step.desc}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Plans */}
              <div data-id="element-7371" data-mp="2jym21icej7c7">
                <h3 className="text-lg font-bold text-[#C9A34E] mb-2 flex items-center gap-2" data-id="element-7372" data-mp="1wab4ao205ns5">
                  <Crown className="w-5 h-5" data-id="element-7373" data-mp="1rc15m3h2k51s" />
                  Nuestros Planes
                </h3>
                <p className="text-white/50 text-sm mb-6" data-id="element-7374" data-mp="3re97yaxupxxm">
                  Elegí el que mejor se ajuste a tu restaurante. Siempre podés
                  escalar después.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-7375" data-mp="4yogy5c7po7b">
                  {/* Fase 1 */}
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3" data-id="element-7376" data-mp="90jeqzuxpjgc">
                    <div className="flex items-center gap-2" data-id="element-7377" data-mp="38fesezqkctge">
                      <QrCode className="w-5 h-5 text-[#C9A34E]" data-id="element-7378" data-mp="38h7zgg539fox" />
                      <h4 className="font-bold text-white text-sm" data-id="element-7379" data-mp="3df13b2ix5xm1">
                        Menú Digital Fase 1
                      </h4>
                    </div>
                    <p className="text-xs text-white/50 leading-relaxed" data-id="element-7380" data-mp="3meo87yauxlju">
                      Lo esencial para arrancar digital.
                    </p>
                    <ul className="space-y-2 text-sm text-white/70" data-id="element-7381" data-mp="wpcfraqeyhht">
                      <li className="flex items-start gap-2" data-id="element-7382" data-mp="11nnp7kcsqpzi">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-7383" data-mp="1bsm5znnvv9hk" />
                        <span data-id="element-7384" data-mp="16tizrzw62x80">Tu menú digital con fotos y precios</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-7385" data-mp="122svkpzh90ew">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-7386" data-mp="1219cysqcmd9y" />
                        <span data-id="element-7387" data-mp="y0pgzshihfpe">Código QR para mesas o redes</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-7388" data-mp="t3pdnpc1qr1z">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-7389" data-mp="o2i3xkqcfhso" />
                        <span data-id="element-7390" data-mp="j3expwymn5j4">Link propio de tu restaurante</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-7391" data-mp="f2mpn71hr6of">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-7392" data-mp="f4fxgpnubwy0" />
                        <span data-id="element-7393" data-mp="2dh6erhjacrol">Configuración inicial incluida</span>
                      </li>
                    </ul>
                    <div className="pt-2 border-t border-white/5" data-id="element-7394" data-mp="29ejmxgmm76re">
                      <p className="text-[10px] uppercase tracking-wider text-white/30 font-medium" data-id="element-7395" data-mp="2gk3w421f8d1i">
                        Ideal para
                      </p>
                      <p className="text-xs text-white/50" data-id="element-7396" data-mp="27jfllhb8kutd">
                        Restaurantes que quieren mostrar su menú de forma
                        profesional
                      </p>
                    </div>
                  </div>

                  {/* Fase 2 */}
                  <div className="bg-[#C9A34E]/5 border border-[#C9A34E]/20 rounded-2xl p-5 space-y-3 relative" data-id="element-7397" data-mp="22jt7z6rhxck1">
                    <div className="absolute -top-2.5 right-4 bg-[#C9A34E] text-[#0B0B0B] text-[10px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wider" data-id="element-7398" data-mp="1nvf4ctm99k2f">
                      Popular
                    </div>
                    <div className="flex items-center gap-2" data-id="element-7399" data-mp="1ev9y4msssrpu">
                      <UtensilsCrossed className="w-5 h-5 text-[#C9A34E]" data-id="element-7400" data-mp="1jtbo7ron5rat" />
                      <h4 className="font-bold text-white text-sm" data-id="element-7401" data-mp="1jved28e25edy">
                        Menú Digital Fase 2
                      </h4>
                    </div>
                    <p className="text-xs text-white/50 leading-relaxed" data-id="element-7402" data-mp="v9qijqryr431">
                      Menú + sistema de pedidos real.
                    </p>
                    <ul className="space-y-2 text-sm text-white/70" data-id="element-7403" data-mp="156mgx3o5e9tv">
                      <li className="flex items-start gap-2" data-id="element-7404" data-mp="155bfhai6de5x">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-7405" data-mp="1j5m92lavd6us" />
                        <span data-id="element-7406" data-mp="1e82rpcivkdym">Todo lo de Fase 1</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-7407" data-mp="1svxj8pl5cca4">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-7408" data-mp="1sya0t1mzskmn" />
                        <span data-id="element-7409" data-mp="1nz77ufkae4v1">Pedidos directos desde el menú</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-7410" data-mp="21x56mcm4jspf">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-7411" data-mp="1wyk94ky9bn4o" />
                        <span data-id="element-7412" data-mp="ul5wvyu7xisr">Pantalla de cocina (KDS)</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-7413" data-mp="zixxricucf93">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-7414" data-mp="zh3ahxe3kb1u" />
                        <span data-id="element-7415" data-mp="13iydr7fl8yi9">Reportes básicos de ventas</span>
                      </li>
                    </ul>
                    <div className="pt-2 border-t border-[#C9A34E]/10" data-id="element-7416" data-mp="1daumrdj7q919">
                      <p className="text-[10px] uppercase tracking-wider text-white/30 font-medium" data-id="element-7417" data-mp="1dam9vln3b3q2">
                        Ideal para
                      </p>
                      <p className="text-xs text-white/50" data-id="element-7418" data-mp="1na3c1t34uzha">
                        Restaurantes que quieren recibir pedidos organizados y
                        controlar su cocina
                      </p>
                    </div>
                  </div>

                  {/* Completo */}
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3" data-id="element-7419" data-mp="3t18gk5cio36k">
                    <div className="flex items-center gap-2" data-id="element-7420" data-mp="3lir17ihylu5o">
                      <BarChart3 className="w-5 h-5 text-[#C9A34E]" data-id="element-7421" data-mp="3lkbz9i3hfv8a" />
                      <h4 className="font-bold text-white text-sm" data-id="element-7422" data-mp="3gq7riwme7j65">
                        Menú Digital Completo
                      </h4>
                    </div>
                    <p className="text-xs text-white/50 leading-relaxed" data-id="element-7423" data-mp="37rvqs1rtb4zz">
                      El sistema integral para crecer.
                    </p>
                    <ul className="space-y-2 text-sm text-white/70" data-id="element-7424" data-mp="89uy5fh9q9hx">
                      <li className="flex items-start gap-2" data-id="element-7425" data-mp="3e5up124o8gc">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-7426" data-mp="3vg5h9gzz0gxg" />
                        <span data-id="element-7427" data-mp="3qi3mvzx3gv6o">Todo lo de Fase 2</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-7428" data-mp="2k1i5qv7na6ew">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-7429" data-mp="2k330olxvfrda" />
                        <span data-id="element-7430" data-mp="2f0u7ppl00pb6">Reportes avanzados y métricas</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-7431" data-mp="iyh8dasa1omo">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-7432" data-mp="n1dnusmit4me" />
                        <span data-id="element-7433" data-mp="91mdr2z4k8do">Programa de lealtad para clientes</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-7434" data-mp="dzxrvpwgd51p">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-7435" data-mp="e2a3zmfj4l6u" />
                        <span data-id="element-7436" data-mp="11ruzf4svdax0">Inventario y control de stock</span>
                      </li>
                    </ul>
                    <div className="pt-2 border-t border-white/5" data-id="element-7437" data-mp="16qo6aedni3o7">
                      <p className="text-[10px] uppercase tracking-wider text-white/30 font-medium" data-id="element-7438" data-mp="rzff40u3oa8y">
                        Ideal para
                      </p>
                      <p className="text-xs text-white/50" data-id="element-7439" data-mp="1kocnnc8iesoi">
                        Restaurantes que quieren el paquete completo: operación,
                        datos y fidelización
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Marketplace Connection */}
              <div className="bg-gradient-to-br from-[#C9A34E]/10 to-transparent border border-[#C9A34E]/20 rounded-2xl p-6" data-id="element-7440" data-mp="356evvihfznhn">
                <h3 className="text-lg font-bold text-[#C9A34E] mb-3 flex items-center gap-2" data-id="element-7441" data-mp="3ewgf9wyuohq8">
                  <Crown className="w-5 h-5" data-id="element-7442" data-mp="2r6vxviichsj2" />
                  Marketplace NOIZA — Más visibilidad para tu restaurante
                </h3>
                <p className="text-white/70 leading-relaxed mb-4" data-id="element-7443" data-mp="2m8ue0vd3hp6j">
                  Además de tu menú digital propio, al tener cualquier plan
                  activo con NOIZA tu restaurante aparece automáticamente en
                  nuestro{' '}
                  <strong className="text-white" data-id="element-7444" data-mp="2vatu9lgacp8f">
                    Marketplace gastronómico
                  </strong>{' '}
                  en <strong className="text-white" data-id="element-7445" data-mp="airvipq9kj01">noizacr.com</strong>. Esto
                  significa que clientes nuevos que no te conocen pueden
                  descubrirte, ver tu menú y hacerte pedidos directamente.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-7446" data-mp="3rh07pty06191">
                  <div className="flex items-start gap-2" data-id="element-7447" data-mp="a83e57wxuexv">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-7448" data-mp="57yz7mbohs9r" />
                    <span className="text-sm text-white/70" data-id="element-7449" data-mp="j7qk870m48x9">
                      Tu restaurante listado en el marketplace de NOIZA
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-7450" data-mp="e6koe0bno5ji">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-7451" data-mp="swi6pzmwm10s" />
                    <span className="text-sm text-white/70" data-id="element-7452" data-mp="nzhz3kaeoq7k">
                      Clientes nuevos te descubren sin que hagas nada
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-7453" data-mp="xx66b9p77q6m">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-7454" data-mp="xujqkms5m4db" />
                    <span className="text-sm text-white/70" data-id="element-7455" data-mp="1giu6ul229fz6">
                      Pedidos directos desde la página del marketplace
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-7456" data-mp="17jqmldr5quj3">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-7457" data-mp="1cits0zb1v2rl" />
                    <span className="text-sm text-white/70" data-id="element-7458" data-mp="xup51j4ys530">
                      Sin comisiones extra — ya está incluido en tu plan
                    </span>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-white/5 rounded-xl border border-white/5" data-id="element-7459" data-mp="ouak4kvwws01">
                  <p className="text-xs text-white/50" data-id="element-7460" data-mp="sv2trf8p4ywu">
                    <strong className="text-white/70" data-id="element-7461" data-mp="svm2q6gdccyq">Requisito:</strong> Para
                    aparecer en el marketplace, tu restaurante debe tener un
                    menú digital activo con NOIZA (cualquier plan). Así
                    garantizamos que todos los restaurantes listados ofrecen una
                    experiencia digital de calidad a los clientes.
                  </p>
                </div>
              </div>

              {/* CTA */}
              <div className="text-center pt-4" data-id="element-7462" data-mp="2cqqjygcx7cqi">
                <button onClick={() => {
              setShowHowItWorks(false);
              handleNext();
            }} className="px-8 py-4 bg-[#C9A34E] text-[#0B0B0B] rounded-2xl font-bold text-lg hover:bg-[#d4b365] transition-colors inline-flex items-center gap-2" data-id="element-7463" data-mp="2cpfg6gjh64ze">
                  Aplicar ahora <ChevronRight className="w-5 h-5" data-id="element-7464" data-mp="2vgyh8htxv8ys" />
                </button>
                <p className="text-white/40 text-xs mt-3" data-id="element-7465" data-mp="2qjg5yksg8hnm">
                  Aplicar es gratis y sin compromiso. Nuestro equipo te contacta
                  para explicarte todo.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
  const renderHero = () => <motion.div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 text-center" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0,
    y: -50
  }} data-id="element-7466" data-mp="151e42f4epf2i">
      {/* Logo Area */}
      <motion.div className="mb-8 relative" initial={{
      scale: 0.8,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      duration: 0.8,
      ease: 'easeOut'
    }} data-id="element-7467" data-mp="1jqa6nwnp1zvq">
        <div className="absolute inset-0 bg-[#C9A34E] blur-[60px] opacity-30 rounded-full" data-id="element-7468" data-mp="1xp1itdizxbh8" />
        <h1 className="text-5xl md:text-7xl font-black text-white tracking-tighter relative z-10" style={{
        textShadow: '0 0 20px rgba(201,163,78,0.5)'
      }} data-id="element-7469" data-mp="22ma4ho3crxn8">
          NOIZA
        </h1>
      </motion.div>

      {/* Headlines */}
      <motion.h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white max-w-4xl leading-tight mb-6" initial={{
      y: 20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      delay: 0.2,
      duration: 0.8
    }} data-id="element-7470" data-mp="1ypw7qeq1fbg8">
        Tu restaurante ya no necesita solo un menú.{' '}
        <span className="text-[#C9A34E]" data-id="element-7471" data-mp="1f1wu8w439q74">Necesita un sistema.</span>
      </motion.h2>

      <motion.p className="text-lg md:text-xl text-white/70 max-w-2xl mb-12" initial={{
      y: 20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      delay: 0.4,
      duration: 0.8
    }} data-id="element-7472" data-mp="1b1mrdsblffwh">
        Aplica para unirte al ecosistema digital más avanzado para restaurantes.
        Pedidos, operación, KDS y crecimiento en una sola plataforma.
      </motion.p>

      {/* CTAs */}
      <motion.div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto mb-16" initial={{
      y: 20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      delay: 0.6,
      duration: 0.8
    }} data-id="element-7473" data-mp="32zxn2crrjltc">
        <button onClick={handleNext} className="relative group px-8 py-4 bg-[#C9A34E] text-[#0B0B0B] rounded-2xl font-bold text-lg overflow-hidden transition-transform hover:scale-105" data-id="element-7474" data-mp="2yzx9svgehgnv">
          <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" data-id="element-7475" data-mp="3qpi8d2be2v5s" />
          <span className="relative z-10 flex items-center justify-center gap-2" data-id="element-7476" data-mp="3hpdc9obnbj6x">
            Aplicar ahora <ChevronRight className="w-5 h-5" data-id="element-7477" data-mp="21b3puzqys4iq" />
          </span>
        </button>
        <button onClick={() => setShowHowItWorks(true)} className="px-8 py-4 bg-white/5 hover:bg-white/10 text-white border border-white/10 rounded-2xl font-bold text-lg transition-all backdrop-blur-sm" data-id="element-7478" data-mp="21d6qedpqtg1p">
          Ver cómo funciona
        </button>
      </motion.div>

      {/* Trust Pills */}
      <motion.div className="flex flex-wrap justify-center gap-3 max-w-3xl" initial="hidden" animate="visible" variants={{
      visible: {
        transition: {
          staggerChildren: 0.1,
          delayChildren: 0.8
        }
      }
    }} data-id="element-7479" data-mp="1dfs5t7dxkuho">
        {['Pedidos por WhatsApp', 'Tracking en tiempo real', 'KDS y operación', 'Marketplace gastronómico', 'Tecnología para restaurantes'].map((pill, i) => <motion.div key={i} variants={{
        hidden: {
          opacity: 0,
          y: 10
        },
        visible: {
          opacity: 1,
          y: 0
        }
      }} className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm backdrop-blur-md" data-id="element-7480" data-mp="pj4odfcdhi5z" data-mpi={i}>
            {pill}
          </motion.div>)}
      </motion.div>
    </motion.div>;
  const renderFormStep = () => {
    const slideVariants = {
      enter: {
        x: 50,
        opacity: 0
      },
      center: {
        x: 0,
        opacity: 1
      },
      exit: {
        x: -50,
        opacity: 0
      }
    };
    return <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-12" data-id="element-7481" data-mp="28iu3292pyh46">
        <motion.div className="w-full max-w-xl bg-[#141414]/80 backdrop-blur-2xl border border-white/10 rounded-3xl shadow-2xl overflow-hidden" initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} data-id="element-7482" data-mp="2ieovfvwqvqk5">
          {/* Progress Bar */}
          <div className="h-1.5 w-full bg-white/5" data-id="element-7483" data-mp="nph7jqrfs6y9">
            <motion.div className="h-full bg-[#C9A34E]" initial={{
            width: 0
          }} animate={{
            width: `${step / 5 * 100}%`
          }} transition={{
            type: 'spring',
            damping: 20
          }} data-id="element-7484" data-mp="ita0tvr7urf9" />
          </div>

          <div className="p-6 md:p-10" data-id="element-7485" data-mp="11j047791olis">
            {/* Header */}
            <div className="flex items-center mb-8" data-id="element-7486" data-mp="11igolx6va97x">
              <button onClick={handleBack} className="p-2 -ml-2 text-white/50 hover:text-white transition-colors" data-id="element-7487" data-mp="1ggsglqjaotsr">
                <ArrowLeft className="w-6 h-6" data-id="element-7488" data-mp="1bf37whjjos7u" />
              </button>
              <div className="ml-auto text-sm font-bold text-[#C9A34E]" data-id="element-7489" data-mp="3i85lifl9j1ot">
                Paso {step} de 5
              </div>
            </div>

            {/* Content */}
            <AnimatePresence mode="wait" data-id="element-7490" data-mp="1sq8c9cyuc0ww">
              <motion.div key={step} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
              type: 'spring',
              damping: 25,
              stiffness: 200
            }} className="min-h-[300px]" data-id="element-7491" data-mp="1sr9z0ep7ybug">
                {step === 1 && <div className="space-y-6" data-id="element-7492" data-mp="1e1m01k4ifp9w">
                    <h3 className="text-2xl font-bold text-white mb-6" data-id="element-7493" data-mp="1e2dirud0dsir">
                      Empecemos con lo básico
                    </h3>
                    <div data-id="element-7494" data-mp="1045oju7cx5ba">
                      <label className="block text-sm font-medium text-white/60 mb-2" data-id="element-7495" data-mp="150e2535woqgc">
                        Tu nombre completo
                      </label>
                      <input type="text" value={formData.owner_name} onChange={e => setFormData({
                    ...formData,
                    owner_name: e.target.value
                  })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white text-lg focus:outline-none focus:border-[#C9A34E] focus:ring-1 focus:ring-[#C9A34E] transition-all" placeholder="Ej. Juan Pérez" autoFocus data-id="element-7496" data-mp="2udrdhccfreqy" />
                    </div>
                    <div data-id="element-7497" data-mp="ssumuw9twh56">
                      <label className="block text-sm font-medium text-white/60 mb-2" data-id="element-7498" data-mp="xq47lym9w4mo">
                        Nombre del restaurante
                      </label>
                      <input type="text" value={formData.restaurant_name} onChange={e => setFormData({
                    ...formData,
                    restaurant_name: e.target.value
                  })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white text-lg focus:outline-none focus:border-[#C9A34E] focus:ring-1 focus:ring-[#C9A34E] transition-all" placeholder="Ej. La Pizzería" data-id="element-7499" data-mp="12o60fb9i3ibc" />
                    </div>
                  </div>}

                {step === 2 && <div className="space-y-6" data-id="element-7500" data-mp="3aw16tyy6mrs4">
                    <h3 className="text-2xl font-bold text-white mb-6" data-id="element-7501" data-mp="3axc7vr08tlfj">
                      ¿Cómo te contactamos?
                    </h3>
                    <div data-id="element-7502" data-mp="30ymzqpy5ffch">
                      <label className="block text-sm font-medium text-white/60 mb-2" data-id="element-7503" data-mp="30xd03wbndz3q">
                        Número de WhatsApp
                      </label>
                      <input type="tel" value={formData.whatsapp} onChange={e => setFormData({
                    ...formData,
                    whatsapp: e.target.value
                  })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white text-lg focus:outline-none focus:border-[#C9A34E] focus:ring-1 focus:ring-[#C9A34E] transition-all" placeholder="88888888" autoFocus data-id="element-7504" data-mp="1pj7smtc8773n" />
                    </div>
                    <div data-id="element-7505" data-mp="2omgr27hnylw">
                      <label className="block text-sm font-medium text-white/60 mb-2" data-id="element-7506" data-mp="3l0rcofwpig1m">
                        Instagram o Facebook (Opcional)
                      </label>
                      <input type="text" value={formData.social_handle} onChange={e => setFormData({
                    ...formData,
                    social_handle: e.target.value
                  })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white text-lg focus:outline-none focus:border-[#C9A34E] focus:ring-1 focus:ring-[#C9A34E] transition-all" placeholder="@turestaurante" data-id="element-7507" data-mp="3pxhmwmltyhf4" />
                    </div>
                  </div>}

                {step === 3 && <div className="space-y-6" data-id="element-7508" data-mp="3bdoryrxxlr65">
                    <h3 className="text-2xl font-bold text-white mb-6" data-id="element-7509" data-mp="3bassv0ackwg4">
                      ¿Qué tipo de negocio tienes?
                    </h3>
                    <div className="grid grid-cols-2 gap-3" data-id="element-7510" data-mp="3k4p43k1z4ogr">
                      {(['Restaurante', 'Cafetería', 'Pizzería', 'Comida rápida', 'Panadería', 'Otro'] as BusinessType[]).map(type => <button key={type} onClick={() => setFormData({
                    ...formData,
                    business_type: type
                  })} className={`p-4 rounded-xl border text-left transition-all ${formData.business_type === type ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-7511" data-mp="1we7ziwby9ijc">
                          <span className="font-medium" data-id="element-7512" data-mp="1nfe8dlhe1kd6">{type}</span>
                        </button>)}
                    </div>
                  </div>}

                {step === 4 && <div className="space-y-8" data-id="element-7513" data-mp="2ysyfqxndrvxv">
                    <h3 className="text-2xl font-bold text-white mb-6" data-id="element-7514" data-mp="n6xzapxoyq1a">
                      Operación actual
                    </h3>

                    <div data-id="element-7515" data-mp="i8w63c6jxac3">
                      <label className="block text-sm font-medium text-white/60 mb-3" data-id="element-7516" data-mp="i8mr0jn1onl6">
                        ¿Ya reciben pedidos por WhatsApp?
                      </label>
                      <div className="grid grid-cols-2 gap-3" data-id="element-7517" data-mp="8h0ac7rxtwb1">
                        <button onClick={() => setFormData({
                      ...formData,
                      uses_whatsapp_orders: true
                    })} className={`p-4 rounded-xl border text-center transition-all ${formData.uses_whatsapp_orders === true ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-7518" data-mp="3ioyjri31bq6">
                          Sí
                        </button>
                        <button onClick={() => setFormData({
                      ...formData,
                      uses_whatsapp_orders: false
                    })} className={`p-4 rounded-xl border text-center transition-all ${formData.uses_whatsapp_orders === false ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-7519" data-mp="3edoto77n1hng">
                          No
                        </button>
                      </div>
                    </div>

                    <div data-id="element-7520" data-mp="3n7tiym74il0v">
                      <label className="block text-sm font-medium text-white/60 mb-3" data-id="element-7521" data-mp="3s4trr4vcy1vo">
                        ¿Tienen menú digital hoy?
                      </label>
                      <div className="grid grid-cols-2 gap-3" data-id="element-7522" data-mp="2bp8xa4a76tak">
                        <button onClick={() => setFormData({
                      ...formData,
                      has_digital_menu: true
                    })} className={`p-4 rounded-xl border text-center transition-all ${formData.has_digital_menu === true ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-7523" data-mp="2gqpvtt57m3nz">
                          Sí
                        </button>
                        <button onClick={() => setFormData({
                      ...formData,
                      has_digital_menu: false
                    })} className={`p-4 rounded-xl border text-center transition-all ${formData.has_digital_menu === false ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-7524" data-mp="b3igavjbq0b3">
                          No
                        </button>
                      </div>
                    </div>
                  </div>}

                {step === 5 && <div className="space-y-6" data-id="element-7525" data-mp="q63xp6sfxrrt">
                    <h3 className="text-2xl font-bold text-white mb-2" data-id="element-7526" data-mp="v69qwreczmc0">
                      ¿Qué les gustaría mejorar primero?
                    </h3>
                    <p className="text-white/50 text-sm mb-6" data-id="element-7527" data-mp="105cojqs0eq9y">
                      Puedes seleccionar varias opciones
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-7528" data-mp="19vo1g2to89sz">
                      {(['Más pedidos', 'Mejor control de cocina', 'Menú digital', 'Delivery / express', 'Orden y operación', 'Todo lo anterior'] as MainGoal[]).map(goal => {
                    const isSelected = formData.main_goal.includes(goal);
                    return <button key={goal} onClick={() => {
                      if (goal === 'Todo lo anterior') {
                        setFormData({
                          ...formData,
                          main_goal: ['Todo lo anterior']
                        });
                      } else {
                        const newGoals = isSelected ? formData.main_goal.filter(g => g !== goal) : [...formData.main_goal.filter(g => g !== 'Todo lo anterior'), goal];
                        setFormData({
                          ...formData,
                          main_goal: newGoals
                        });
                      }
                    }} className={`p-4 rounded-xl border text-left transition-all ${isSelected ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-7529" data-mp="1o0oiqbyrbgoq">
                            <span className="font-medium text-sm" data-id="element-7530" data-mp="66v1d3shb9hn">{goal}</span>
                          </button>;
                  })}
                    </div>
                  </div>}
              </motion.div>
            </AnimatePresence>

            {/* Footer Actions */}
            <div className="mt-10 pt-6 border-t border-white/10" data-id="element-7531" data-mp="1vl9s1tn256fs">
              {error && <p className="text-red-400 text-sm mb-4 text-center" data-id="element-7532" data-mp="1rjo9psrr7zy7">{error}</p>}

              {step < 5 ? <button onClick={handleNext} disabled={!isStepValid()} className="w-full py-4 bg-[#C9A34E] text-[#0B0B0B] rounded-xl font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#d4b365]" data-id="element-7533" data-mp="3ei8nagqrfu1q">
                  Continuar
                </button> : <button onClick={handleSubmit} disabled={!isStepValid() || isSubmitting} className="w-full py-4 bg-[#C9A34E] text-[#0B0B0B] rounded-xl font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#d4b365] flex items-center justify-center gap-2" data-id="element-7534" data-mp="2qmw6ifncp9cx">
                  {isSubmitting ? <>
                      <Loader2 className="w-5 h-5 animate-spin" data-id="element-7535" data-mp="jttswhlmuzvy" /> Enviando...
                    </> : 'Enviar solicitud'}
                </button>}
            </div>
          </div>
        </motion.div>
      </div>;
  };
  const renderSuccess = () => <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 text-center" data-id="element-7536" data-mp="2pucvbhbwdcqw">
      <motion.div className="w-full max-w-md bg-[#141414]/80 backdrop-blur-2xl border border-white/10 rounded-3xl p-10 shadow-2xl" initial={{
      scale: 0.9,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} data-id="element-7537" data-mp="2kxmhz1rh8v96">
        <motion.div className="w-20 h-20 bg-[#C9A34E]/20 rounded-full flex items-center justify-center mx-auto mb-6" initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        delay: 0.2,
        type: 'spring'
      }} data-id="element-7538" data-mp="2251trxmq3ebp">
          <CheckCircle2 className="w-10 h-10 text-[#C9A34E]" data-id="element-7539" data-mp="3l2okecvh8u7t" />
        </motion.div>

        <h2 className="text-3xl font-bold text-white mb-4" data-id="element-7540" data-mp="3pxay4mp16yv2">
          Recibimos tu solicitud
        </h2>
        <p className="text-white/70 mb-8 leading-relaxed" data-id="element-7541" data-mp="3c1qhxz0osurc">
          Si tu restaurante encaja con el ecosistema NOIZA, nuestro equipo se
          pondrá en contacto contigo muy pronto.
        </p>

        <a href="https://wa.me/50688888888" target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 w-full py-4 bg-[#25D366] text-white rounded-xl font-bold text-lg hover:bg-[#20bd5a] transition-colors" data-id="element-7542" data-mp="7nilpql7yzui">
          <MessageCircle className="w-5 h-5" data-id="element-7543" data-mp="3m6ph27ubgdc" />
          Hablar con NOIZA
        </a>
      </motion.div>
    </div>;
  return <div className="min-h-screen bg-[#0B0B0B] font-sans selection:bg-[#C9A34E]/30 text-white" data-id="element-7544" data-mp="2jhictnx05f6n">
      {renderFloatingBackground()}
      {renderHowItWorksModal()}

      <AnimatePresence mode="wait" data-id="element-7545" data-mp="33pgmfvcjaqfm">
        {step === 0 && <motion.div key="hero" className="w-full" data-id="element-7546" data-mp="2ywnkff8g9w78">
            {renderHero()}
          </motion.div>}
        {step > 0 && step < 6 && <motion.div key="form" className="w-full" data-id="element-7547" data-mp="2tycal4i5nlp0">
            {renderFormStep()}
          </motion.div>}
        {step === 6 && <motion.div key="success" className="w-full" data-id="element-7548" data-mp="3mn9qj0yw96el">
            {renderSuccess()}
          </motion.div>}
      </AnimatePresence>
    </div>;
}